import { useState } from "react";
import tasksService from "../../services/tasks";
import "./Trackorder.css";

export default function Trackorder() {
  const [orderId, setOrderId] = useState("");
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    if (e.key === "Enter" && orderId.trim()) {
      await fetchOrder(orderId.trim());
    }
  };

  const handleSearchClick = () => {
    if (orderId.trim()) {
      fetchOrder(orderId.trim());
    }
  };

  const fetchOrder = async (id) => {
    try {
      setLoading(true);
      setError('');

      // Clean the tracking ID - remove any leading # or /
      const cleanId = id.replace(/^[/#]+/, '');

      const trackingData = await tasksService.trackOrder(cleanId);
      setOrderData(trackingData);
    } catch (err) {
      setError(err.message || 'Failed to track order. Please check the Order ID.');
      setOrderData(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pending': '#FFA500',
      'Assigned': '#3498DB',
      'In Transit': '#9B59B6',
      'Delivered': '#27AE60',
      'Exception': '#E74C3C',
      'Returned': '#95A5A6'
    };
    return colors[status] || '#666';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Pending': '⏳',
      'Assigned': '👤',
      'In Transit': '🚚',
      'Delivered': '✅',
      'Exception': '⚠️',
      'Returned': '↩️'
    };
    return icons[status] || '📦';
  };

  return (
    <div className="trackorder-page-container">
      {/* Header Section */}
      <section className="mq-dashboard-header">
        <div>
          <p className="mq-dashboard-welcome-label">Welcome Back,</p>
          <h1 className="mq-dashboard-welcome-name">Track your order</h1>
        </div>
      </section>

      {error && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          borderRadius: '4px',
          color: '#c33',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      {/* Main Card */}
      <div className="trackorder-card-wrapper">
        {/* Order Tracking Section */}
        <div className="trackorder-section">
          <h2 className="trackorder-section-title">Order Tracking</h2>

          {/* Search Input */}
          <div className="trackorder-search-container">
            <input
              type="text"
              className="trackorder-search-input"
              placeholder="Type in order ID (e.g., #5161 or 5161) and press Enter"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              onKeyDown={handleSearch}
              disabled={loading}
            />
            <button
              className="trackorder-search-btn"
              onClick={handleSearchClick}
              disabled={loading || !orderId.trim()}
            >
              {loading ? 'Searching...' : 'Track Order'}
            </button>
          </div>

          {loading && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6366f1' }}>
              Loading order information...
            </div>
          )}

          {!loading && !orderData && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              Enter an order ID above and press Enter to track your order
            </div>
          )}

          {!loading && orderData && (
            <div className="trackorder-results">
              {/* Status Card */}
              <div className="trackorder-status-card">
                <div className="trackorder-status-header">
                  <span className="trackorder-status-icon-large">
                    {getStatusIcon(orderData.status)}
                  </span>
                  <div className="trackorder-status-info">
                    <h3 className="trackorder-job-id">Order {orderData.jobId}</h3>
                    <div
                      className="trackorder-status-badge"
                      style={{ backgroundColor: getStatusColor(orderData.status) }}
                    >
                      {orderData.status}
                    </div>
                  </div>
                </div>
              </div>

              {/* Route Information */}
              <div className="trackorder-route-card">
                <h3 className="trackorder-card-title">Delivery Route</h3>
                <div className="trackorder-route-details">
                  <div className="trackorder-location">
                    <div className="trackorder-location-icon pickup">📍</div>
                    <div className="trackorder-location-info">
                      <p className="trackorder-location-label">Pickup Location</p>
                      <p className="trackorder-location-name">{orderData.from?.name || 'N/A'}</p>
                      <p className="trackorder-location-address">{orderData.from?.address || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="trackorder-route-line"></div>

                  <div className="trackorder-location">
                    <div className="trackorder-location-icon delivery">🎯</div>
                    <div className="trackorder-location-info">
                      <p className="trackorder-location-label">Delivery Location</p>
                      <p className="trackorder-location-name">{orderData.to?.name || 'N/A'}</p>
                      <p className="trackorder-location-address">{orderData.to?.address || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Driver Information */}
              {orderData.driver && (
                <div className="trackorder-driver-card">
                  <h3 className="trackorder-card-title">Assigned Driver</h3>
                  <div className="trackorder-driver-info">
                    <div className="trackorder-driver-avatar">
                      {orderData.driver.firstName?.charAt(0)}{orderData.driver.lastName?.charAt(0)}
                    </div>
                    <div>
                      <p className="trackorder-driver-name">
                        {orderData.driver.firstName} {orderData.driver.lastName}
                      </p>
                      {orderData.driver.vehicle && (
                        <p className="trackorder-driver-vehicle">
                          {orderData.driver.vehicle.make} {orderData.driver.vehicle.model} - {orderData.driver.vehicle.licensePlate}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Timeline */}
              <div className="trackorder-timeline-card">
                <h3 className="trackorder-card-title">Order Timeline</h3>
                <div className="trackorder-timeline-history">
                  {orderData.statusHistory && orderData.statusHistory.length > 0 ? (
                    orderData.statusHistory
                      .slice()
                      .reverse()
                      .map((history, index) => (
                        <div key={index} className="trackorder-timeline-item">
                          <div className="trackorder-timeline-marker" style={{ backgroundColor: getStatusColor(history.status) }}>
                            <span>{getStatusIcon(history.status)}</span>
                          </div>
                          <div className="trackorder-timeline-content">
                            <p className="trackorder-timeline-status">{history.status}</p>
                            <p className="trackorder-timeline-time">
                              {new Date(history.timestamp).toLocaleString()}
                            </p>
                            {history.notes && (
                              <p className="trackorder-timeline-notes">{history.notes}</p>
                            )}
                          </div>
                        </div>
                      ))
                  ) : (
                    <p className="trackorder-no-history">No status updates available</p>
                  )}
                </div>
              </div>

              {/* Additional Details */}
              <div className="trackorder-details-card">
                <h3 className="trackorder-card-title">Order Details</h3>
                <div className="trackorder-details-grid">
                  {orderData.estimatedArrival && (
                    <div className="trackorder-detail-item">
                      <span className="trackorder-detail-label">Estimated Arrival:</span>
                      <span className="trackorder-detail-value">
                        {new Date(orderData.estimatedArrival).toLocaleString()}
                      </span>
                    </div>
                  )}
                  {orderData.deliveredAt && (
                    <div className="trackorder-detail-item">
                      <span className="trackorder-detail-label">Delivered At:</span>
                      <span className="trackorder-detail-value">
                        {new Date(orderData.deliveredAt).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
